/**
 * The show style's JSON editor (D32): its name and config document, the
 * problems at their paths as the text changes (checked by the view with the
 * engine's show-style schema, `lib/showStyle/config.ts`), save — disabled
 * while the document is invalid, unchanged, or the user may not configure
 * show styles — and delete, whose refusal (a studio's default) is shown.
 */
import type { ConfigProblem } from '../../lib/studio/config.js';
import { ConfigProblemList } from '../studios/ConfigProblemList.js';
import { Button } from '../ui/button.js';
import { Field, FieldError } from '../ui/field.js';
import { Input } from '../ui/input.js';
import { Label } from '../ui/label.js';
import { Textarea } from '../ui/textarea.js';

export type ShowStyleConfigEditorProps = {
  readonly name: string;
  readonly text: string;
  readonly problems: readonly ConfigProblem[];
  readonly problemSource: 'editor' | 'server';
  /** The version the edit is made from. */
  readonly version: number;
  readonly dirty: boolean;
  readonly canSave: boolean;
  readonly pending: boolean;
  /** Why the last delete was refused (a studio's default), or null. */
  readonly deleteError: string | null;
  readonly onName: (name: string) => void;
  readonly onText: (text: string) => void;
  readonly onSave: () => void;
  readonly onRevert: () => void;
  readonly onDelete: () => void;
};

export function ShowStyleConfigEditor(props: ShowStyleConfigEditorProps) {
  const readOnly = !props.canSave;
  const saveable = props.canSave && props.dirty && props.problems.length === 0 && props.name.trim() !== '' && !props.pending;
  return (
    <div className="grid gap-4" data-testid="show-style-config-editor">
      <Field>
        <Label htmlFor="show-style-edit-name">Name</Label>
        <Input id="show-style-edit-name" value={props.name} onChange={(e) => props.onName(e.target.value)} readOnly={readOnly} />
      </Field>
      <Field>
        <Label htmlFor="show-style-edit-config">Config (JSON)</Label>
        <Textarea
          id="show-style-edit-config"
          className="min-h-96 font-mono text-xs"
          spellCheck={false}
          value={props.text}
          onChange={(e) => props.onText(e.target.value)}
          readOnly={readOnly}
          aria-invalid={props.problems.length > 0}
          data-testid="show-style-config-text"
        />
      </Field>
      <ConfigProblemList problems={props.problems} source={props.problemSource} schema="show-style" />
      <FieldError>{props.deleteError}</FieldError>
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">
          Editing version {props.version}
          {readOnly ? ' — read only (configuring show styles needs device.configure tenant-wide)' : ''}
        </span>
        {props.canSave ? (
          <Button variant="destructive" className="ml-auto" onClick={props.onDelete} disabled={props.pending} data-testid="delete-show-style">
            Delete
          </Button>
        ) : null}
        <Button variant="outline" className={props.canSave ? '' : 'ml-auto'} onClick={props.onRevert} disabled={!props.dirty || props.pending}>
          Revert
        </Button>
        <Button onClick={props.onSave} disabled={!saveable} data-testid="save-show-style">
          {props.pending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
